import { AnyAction } from "redux";
import initialStore from "./initialStore";
import { ProjectActions } from "./actionsNames";
import mutateAttributes from "./mutators/mutateAttributes";
import { cloneDeep } from "lodash/fp";
import {getActiveCategory, getAttributesByCategory, getCurrentProject} from "./selectors";
import mutateCustomAttributes from "./mutators/mutateCustomAttributes";

export default (store: IProjectStore = initialStore, action: AnyAction) => {
  switch (action.type) {
    case ProjectActions.SetActive:
      return {
        ...store,
        active: action.active,
        expanded: action.active[0]
      };
    case ProjectActions.SelectAttr:
      return action.custom ? mutateCustomAttributes(store, action):mutateAttributes(store, action);
    case ProjectActions.SetZoom:
      return {
        ...store,
        zoom: action.zoom,
      };
    case ProjectActions.ToggleFlip: {
      return {
        ...store,
        fliped: !store.fliped,
      };
    }
    case ProjectActions.Center:
      return {
        ...store,
        centred: !store.centred,
      };
    case ProjectActions.ChangeWall:
      return {
        ...store,
        wall: (store.wall % 2) + 1,
      };
    case ProjectActions.Reset:
      return {
        ...store,
        wall: 1,
        selectedAttributes: [],
        attributes: store.attributes.filter(a => a.category === Category.Model),
        zoom: 1,
        centred: store.centred,
        id: null,
        editable: true,
        date: null,
        active: menus[0],
        expanded: menus[0][0]
      };
    case ProjectActions.AddProjectToCart:
      return {
        ...store,
        cart: [
          ...store.cart.filter((c) => c.id !== action.project.id),
          action.project,
        ],
      };
    case ProjectActions.ChangeProjectCount:
      return {
        ...store,
        cart: [
          ...(store.cart as any).map((c: any) =>
            c.id !== action.id ? c : { ...c, count: action.count }
          ),
        ],
        loading: true
      };
    case ProjectActions.DeleteCart:
      return {
        ...store,
        cart: [...store.cart.filter((c) => c.id !== action.id)],
      };
    case ProjectActions.LoadHistory:
      return {
        ...store,
        history: action.history,
      };
    case ProjectActions.LoadCart:
      return {
        ...store,
        cart: action.cart,
      };
    case ProjectActions.SetLoaded:
      return {
        ...store,
        loaded: action.loaded,
      };
    case ProjectActions.LoadProject:
      return {
        ...store,
        fliped: action.project.fliped,
        wall: action.project.wall,
        date: action.project.date,
        id: action.project.id,
        selectedAttributes: action.project.components.map((c: any) => c.id),
        editable: action.project.editable,
        loaded: true
      };
    case ProjectActions.SearchDist:
      return {
        ...store,
        searchDist: action.search,
      };
    case ProjectActions.SelectDist:
      return {
        ...store,
        selectedDist: [
          ...store.selectedDist,
          store.distributors.find((d) => d.id === action.id),
        ],
      };
    case ProjectActions.SetForm:
      return {
        ...store,
        form: action.form,
      };
    case ProjectActions.SetServices:
      return {
        ...store,
        installation: action.installation,
        measurement: action.measurement,
      };
    case ProjectActions.RemoveDist:
      return {
        ...store,
        selectedDist: [...store.selectedDist.filter((s) => s.id !== action.id)],
      };
    case ProjectActions.SetAttributes:
      return {
        ...store,
        attributes: action.attr,
        selectedAttributes: action.selected? action.selected.map(s => s.id) : store.selectedAttributes
      };
    case ProjectActions.UpdateHistory:
      return {
        ...store,
        id: action.project.id,
        loaded: true,
        history: store.history.find((h) => h.id === action.project.id)
          ? [
              ...(store.history as any).map((h: any) =>
                h.id !== action.project.id ? h : action.project
              ),
            ]
          : [...store.history, action.project],
      };
    case ProjectActions.UpdateCart:
      return {
        ...store,
        cartId: action.cart.id,
        cart: action.cart.projects,
        loading: false
      };
    case ProjectActions.LoadDist:
      return {
        ...store,
        distributors: action.dist,
      };
    case ProjectActions.RemoveCart:
      return {
        ...store,
        cartId: null,
        cart: [],
      };
    case "deselect all attributes":
      return {
        ...store,
        selectedAttributes: []
      }
    case ProjectActions.SetExpanded:
      return {
        ...store,
        expanded: action.expanded
      }
    case ProjectActions.NextMenu:
      let menu = menus[menus.findIndex(a => a[0].includes(store.active[0])) + 1]
        while(getAttributesByCategory(menu)({project:store}).length === 0){
          menu = menus[menus.findIndex(a => a[0].includes(menu[0])) + 1]
        }
      return {
        ...store,
        active: menu,
        expanded: menu[0]
      }
    default:
      return store;
  }
};

export interface IProjectStore {
  id: string | null;
  cartId: string | null;
  active: Category[];
  attributes: IAttributes[];
  selectedAttributes: number[];
  custom: any
  zoom: number;
  fliped: boolean;
  centred: boolean;
  wall: number;
  date: number | null;
  expanded: string | Category,
  cart: IProject[] | [];
  history: IProject[] | [];
  loaded: boolean;
  distributors: IDistributor[];
  searchDist: string;
  selectedDist: any;
  form: IUserForm;
  loading: boolean
  measurement: boolean;
  installation: boolean;
  editable: boolean;
}

export interface IUserForm {
  name: string;
  email: string;
  phone: string;
  notes: string;
  agree: boolean;
}

export interface IDistributor {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
}

export interface IProject {
  id: string;
  components: IAttributes[];
  count: number;
  date: number;
  wall: number;
  fliped: boolean;
  editable: boolean;
}

export interface IAttributes {
  id: number;
  slug: string;
  name: string;
  description: string;
  category: Category;
  simg: string;
  vimg: string;
  dependency: { [key: string]: number[] };
  price: string;
  tags: string;
  disabled?: boolean;
  default: {[key:string]:string }
}

export enum Category {
  Model = "model",
  Size = 'size',
  Side = 'side',
  Glass = 'glass',
  Color = "color",
  Variant = "variant",
  Handle = "handle",
  Jamb = "jamb",
  Custom = 'custom',
  JambColor = "jambColor",
  JambSize = 'jambSize',
  Adds = "adds",
  HandleColor = 'handleColor',
  AddsColor = "addsColor",
  Signboard = "signboard",
}

const menus = [
  [Category.Model, Category.Variant],
  [Category.Color],
  [Category.Size, Category.Side],
  [Category.Glass],
  [Category.Custom, Category.Jamb, Category.JambSize, Category.JambColor],
  [Category.Handle, Category.HandleColor, Category.Signboard],
  [Category.Adds, Category.AddsColor]
]
